import { useMemo, useState } from 'react';

import { Bot, Headset, Maximize2, Star, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

import { AgentGraphCanvas } from './agent-graph/AgentGraphCanvas';

import type { MultiAgentSectionProps } from './types';

export const MultiAgentSection = ({
  multiAgentEnabled,
  onMultiAgentEnabledChange,
  agentGraph,
  onAgentGraphChange,
  transferTargets,
  customTools,
  title = 'Multi-Agent Mode',
  description = "Enable multiple AI agents that can hand off calls to each other based on context. When on, the configuration's own prompt is ignored and the entry agent's prompt takes its place.",
  editorTitle = 'Multi-Agent Graph Editor',
  hideToggle = false,
}: MultiAgentSectionProps) => {
  const [fullscreen, setFullscreen] = useState(false);

  const graphSummary = useMemo(() => {
    if (!agentGraph?.agents?.length) return null;
    const aiAgents = agentGraph.agents.filter((a) => a.type === 'ai_agent');
    const humanTargets = agentGraph.agents.filter((a) => a.type === 'human');
    const entry = agentGraph.agents.find((a) => a.is_entry);
    const edgeCount = agentGraph.edges?.length ?? 0;
    return { aiAgents, humanTargets, entry, edgeCount };
  }, [agentGraph]);

  return (
    <>
      <section className="bg-card space-y-5 rounded-xl border p-5">
        <div className="flex items-start justify-between border-b pb-3">
          <div>
            <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
            <p className="text-muted-foreground mt-1 text-xs">{description}</p>
          </div>
          {!hideToggle && (
            <Switch
              checked={multiAgentEnabled}
              onCheckedChange={onMultiAgentEnabledChange}
              aria-label="Enable multi-agent mode"
            />
          )}
        </div>

        {multiAgentEnabled && !fullscreen && (
          <div className="space-y-3">
            {graphSummary && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex flex-wrap gap-2">
                  {graphSummary.aiAgents.map((a) => (
                    <div
                      key={a.id || a.name}
                      className="flex items-center gap-1.5 rounded-full bg-indigo-500/10 px-2.5 py-1 text-[11px]"
                    >
                      <Bot size={12} className="text-indigo-400" />
                      <span className="text-indigo-300">{a.name || 'unnamed'}</span>
                      {a.is_entry && <Star size={10} className="fill-yellow-400 text-yellow-400" />}
                    </div>
                  ))}
                  {graphSummary.humanTargets.map((a) => (
                    <div
                      key={a.id || a.name}
                      className="flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px]"
                    >
                      <Headset size={12} className="text-amber-400" />
                      <span className="text-amber-300">{a.name || 'unnamed'}</span>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground text-[10px]">
                  {graphSummary.aiAgents.length} agent
                  {graphSummary.aiAgents.length !== 1 ? 's' : ''}
                  {graphSummary.humanTargets.length > 0 &&
                    ` · ${graphSummary.humanTargets.length} human target${graphSummary.humanTargets.length !== 1 ? 's' : ''}`}
                  {` · ${graphSummary.edgeCount} connection${graphSummary.edgeCount !== 1 ? 's' : ''}`}
                  {graphSummary.entry && ` · Entry: ${graphSummary.entry.name}`}
                </p>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setFullscreen(true)}
            >
              <Maximize2 size={14} className="mr-1.5" />
              Open Agent Graph Editor
            </Button>
          </div>
        )}
      </section>

      {multiAgentEnabled && fullscreen && (
        <div className="bg-background fixed inset-0 z-50 flex flex-col">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <h2 className="text-sm font-semibold">{editorTitle}</h2>
            <Button type="button" variant="ghost" size="sm" onClick={() => setFullscreen(false)}>
              <X size={16} className="mr-1" />
              Exit Editor
            </Button>
          </div>
          <div className="flex-1">
            <AgentGraphCanvas
              value={agentGraph}
              onChange={onAgentGraphChange}
              transferTargets={transferTargets}
              customTools={customTools}
            />
          </div>
        </div>
      )}
    </>
  );
};
